/**
 * StandardDraftContext — single source of truth for the in-flight Standard
 * tab draft (a Voluum-shaped flow: Voluum or Tres Commas) inside the Flow
 * Optimizer drawer.
 *
 * Before this context, both `StandardConfigTabV2` and `PreviewVoluumSection`
 * instantiated their own `useStandardDraft`. That meant a path toggled in the
 * Preview tab editor never reached the live preview API — the two drafts were
 * independent. Lifting the draft here lets the live preview send the in-flight
 * flow as `flowOverride` so weights redistribute immediately.
 */

import {
  createContext,
  useContext,
  useMemo,
  type FC,
  type ReactNode,
} from 'react';

import { useRawFlow } from '../hooks';
import {
  useStandardDraft,
  type UseStandardDraftResult,
} from '../tabs/standard/v2/hooks';
import type { UseQueryResult } from '@tanstack/react-query';
import type { TrackerId, VoluumFlowResource } from '@trafficpulse/shared';

export interface StandardDraftContextValue {
  flowQuery: UseQueryResult<VoluumFlowResource | undefined>;
  draftState: UseStandardDraftResult;
  /** The tracker the flow is read from and applied to (Voluum, Tres Commas). */
  provider: TrackerId;
}

const Ctx = createContext<StandardDraftContextValue | null>(null);

export interface StandardDraftProviderProps {
  flowId: string | undefined;
  provider: TrackerId;
  children: ReactNode;
}

/**
 * IMPORTANT (perf): the context value is memoized so consumers do not re-render
 * on every parent tick. Without this the live preview provider (and every
 * PreviewRowHoverCard underneath it) recomputes on every keystroke / debounce
 * tick, which is visible as preview lag on flows with many paths.
 */
export const StandardDraftProvider: FC<StandardDraftProviderProps> = ({
  flowId,
  provider,
  children,
}) => {
  const flowQuery = useRawFlow(flowId, provider) as UseQueryResult<
    VoluumFlowResource | undefined
  >;
  const draftState = useStandardDraft(flowQuery.data);
  const value = useMemo<StandardDraftContextValue>(
    () => ({ flowQuery, draftState, provider }),
    [flowQuery, draftState, provider],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export function useStandardDraftContext(): StandardDraftContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error(
      'useStandardDraftContext must be used inside <StandardDraftProvider>',
    );
  }
  return ctx;
}

export function useOptionalStandardDraftContext():
  | StandardDraftContextValue
  | null {
  return useContext(Ctx);
}
